import { useCallback, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ButtonProps, ChipProps } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import { ConfirmDialog } from 'src/components/custom-dialog';

/** Optional / empty cell placeholder — keeps column width stable. */
export function StEmpty({ children = '—' }: { children?: ReactNode }) {
  return (
    <Box component="span" sx={{ color: 'text.disabled' }}>
      {children}
    </Box>
  );
}

export function stText(value: unknown, empty: ReactNode = '—'): ReactNode {
  if (value === null || value === undefined || value === '') return <StEmpty>{empty}</StEmpty>;
  return String(value);
}

/** Local timezone `YYYY-MM-DD HH:mm:ss`. Empty → `''` (pair with `stText`). Invalid → raw string. */
export function stFormatDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const d = dayjs(value as string | number | Date);
  if (!d.isValid()) return String(value);
  return d.format('YYYY-MM-DD HH:mm:ss');
}

function shortenId(text: string, head = 8, tail = 4): string {
  if (text.length <= head + tail + 1) return text;
  return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

/** Short ID + hover full value + copy — avoids table overlap. Optional `to` makes the ID a link. */
export function StIdCell({
  value,
  head = 8,
  tail = 4,
  to,
}: {
  value: unknown;
  head?: number;
  tail?: number;
  to?: string;
}) {
  const [copied, setCopied] = useState(false);
  const text = value === null || value === undefined || value === '' ? '' : String(value);
  if (!text) return <StEmpty />;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1000);
  };

  const label = (
    <Typography
      variant="body2"
      component="span"
      noWrap
      sx={{
        fontFamily: 'monospace',
        fontSize: 12,
        cursor: to ? 'pointer' : 'default',
        color: to ? 'primary.main' : 'inherit',
        textDecoration: to ? 'underline' : 'none',
        textUnderlineOffset: 2,
      }}
    >
      {shortenId(text, head, tail)}
    </Typography>
  );

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap={0.25}
      sx={{ maxWidth: 160, verticalAlign: 'middle' }}
    >
      <Tooltip title={text}>
        {to ? (
          <Box
            component={RouterLink}
            to={to}
            onClick={(e) => e.stopPropagation()}
            sx={{ textDecoration: 'none', color: 'inherit', display: 'inline-flex' }}
          >
            {label}
          </Box>
        ) : (
          label
        )}
      </Tooltip>
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** True/False (or custom labels) with success/error chips — matches agent-info. */
export function StBooleanChip({
  value,
  trueLabel = 'True',
  falseLabel = 'False',
  size = 'small',
}: {
  value: boolean | null | undefined;
  trueLabel?: string;
  falseLabel?: string;
  size?: ChipProps['size'];
}) {
  if (value === null || value === undefined) return <StEmpty />;
  return (
    <Chip
      size={size}
      label={value ? trueLabel : falseLabel}
      color={value ? 'success' : 'error'}
    />
  );
}

const STATUS_COLOR: Record<string, ChipProps['color']> = {
  ACTIVE: 'success',
  ENABLED: 'success',
  SUCCESS: 'success',
  OK: 'success',
  SETTLED: 'success',
  CLAIMED_OUT: 'info',
  EXPIRED: 'error',
  FAILURE: 'error',
  FAILED: 'error',
  ERROR: 'error',
  BANNED: 'error',
  DISABLED: 'error',
  ENDED_UNQUALIFIED: 'error',
  DELETED: 'default',
  PENDING: 'warning',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  PENDING_SETTLEMENT: 'warning',
  UPCOMING: 'warning',
  ENDED: 'default',
  INACTIVE: 'default',
};

export function StStatusChip({
  status,
  size = 'small',
}: {
  status: string | null | undefined;
  size?: ChipProps['size'];
}) {
  if (!status) return <StEmpty />;
  const key = String(status).toUpperCase();
  const color = STATUS_COLOR[key] || 'default';
  return <Chip size={size} label={status} color={color} />;
}

/** Text action button — matches agent-info ActionButton. */
export function StActionButton({ sx, ...props }: ButtonProps) {
  return (
    <Button
      variant="text"
      size="small"
      color="primary"
      {...props}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        px: 0.5,
        minWidth: 0,
        whiteSpace: 'nowrap',
        '& .MuiButton-startIcon': { ml: 0 },
        ...sx,
      }}
    />
  );
}

/** Horizontal action row — never stack buttons. */
export function StActionRow({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
      {children}
    </Stack>
  );
}

/** Name on top, short ID below — for room / user cells. */
export function StNameIdCell({
  name,
  id,
}: {
  name?: unknown;
  id?: unknown;
}) {
  const hasName = name !== null && name !== undefined && name !== '';
  const hasId = id !== null && id !== undefined && id !== '';
  if (!hasName && !hasId) return <StEmpty />;

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, maxWidth: 180 }}>
      <Typography variant="body2" noWrap>
        {hasName ? String(name) : '—'}
      </Typography>
      {hasId ? <StIdCell value={id} /> : null}
    </Stack>
  );
}

/** Horizontally scrollable table wrapper — matches user-info / agent-info. */
export function StScrollTable({ children, minWidth = 720 }: { children: ReactNode; minWidth?: number }) {
  return (
    <TableContainer component={Box} sx={{ overflow: 'auto', width: 1 }}>
      <Table
        size="small"
        sx={{
          minWidth,
          '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
        }}
      >
        {children}
      </Table>
    </TableContainer>
  );
}

type StConfirmRequest = {
  title: string;
  content?: ReactNode;
  confirmLabel?: string;
  confirmColor?: ButtonProps['color'];
  onConfirm: () => void | Promise<void>;
};

/** Shared confirm dialog for list row actions. */
export function useStConfirmAction() {
  const [pending, setPending] = useState<StConfirmRequest | null>(null);
  const [busy, setBusy] = useState(false);

  const askConfirm = useCallback((req: StConfirmRequest) => {
    setPending(req);
  }, []);

  const close = useCallback(() => {
    if (busy) return;
    setPending(null);
  }, [busy]);

  const confirmDialog = (
    <ConfirmDialog
      open={!!pending}
      onClose={close}
      title={pending?.title || ''}
      content={pending?.content}
      action={
        <Button
          variant="contained"
          color={pending?.confirmColor || 'primary'}
          disabled={busy}
          onClick={async () => {
            if (!pending) return;
            setBusy(true);
            try {
              await pending.onConfirm();
              setPending(null);
            } finally {
              setBusy(false);
            }
          }}
        >
          {pending?.confirmLabel || 'Confirm'}
        </Button>
      }
    />
  );

  return { askConfirm, confirmDialog };
}
